import Map from "ol/Map.js";
import View from "ol/View.js";
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
  Text,
} from "ol/style.js";
import { Draw, Modify } from "ol/interaction.js";
import { LineString, Point } from "ol/geom.js";
import { OSM, Vector as VectorSource } from "ol/source.js";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer.js";
import { getArea, getLength } from "ol/sphere.js";
import { FeatureToGeoJSON } from "../../lib/featureUtils";
import { key, getProjectionCode, trans } from "../../lib/utils";
import store from "../../stores/store";
import { GeoJSON, WKT } from "ol/format";

const showSegments = true;
const clearPrevious = true;

const editStyle = new Style({
  fill: new Fill({
    color: "rgba(255, 255, 255, 0.2)",
  }),
  stroke: new Stroke({
    color: "rgba(0, 0, 0, 0.5)",
    lineDash: [10, 10],
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: "rgba(0, 0, 0, 0.7)",
    }),
    fill: new Fill({
      color: "rgba(255, 255, 255, 0.2)",
    }),
  }),
});

const editLabelStyle = new Style({
  text: new Text({
    font: "14px Calibri,sans-serif",
    fill: new Fill({
      color: "rgba(255, 255, 255, 1)",
    }),
    backgroundFill: new Fill({
      color: "rgba(0, 0, 0, 0.7)",
    }),
    padding: [3, 3, 3, 3],
    textBaseline: "bottom",
    offsetY: -15,
  }),
  image: new RegularShape({
    radius: 8,
    points: 3,
    angle: Math.PI,
    displacement: [0, 10],
    fill: new Fill({
      color: "rgba(0, 0, 0, 0.7)",
    }),
  }),
});

const editTipStyle = new Style({
  text: new Text({
    font: "12px Calibri,sans-serif",
    fill: new Fill({
      color: "rgba(255, 255, 255, 1)",
    }),
    backgroundFill: new Fill({
      color: "rgba(0, 0, 0, 0.4)",
    }),
    padding: [2, 2, 2, 2],
    textAlign: "left",
    offsetX: 15,
  }),
});

const editModifyStyle = new Style({
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: "rgba(0, 0, 0, 0.7)",
    }),
    fill: new Fill({
      color: "rgba(0, 0, 0, 0.4)",
    }),
  }),
  text: new Text({
    text: "Drag to modify",
    font: "12px Calibri,sans-serif",
    fill: new Fill({
      color: "rgba(255, 255, 255, 1)",
    }),
    backgroundFill: new Fill({
      color: "rgba(0, 0, 0, 0.7)",
    }),
    padding: [2, 2, 2, 2],
    textAlign: "left",
    offsetX: 15,
  }),
});

const editSegmentStyle = new Style({
  text: new Text({
    font: "12px Calibri,sans-serif",
    fill: new Fill({
      color: "rgba(255, 255, 255, 1)",
    }),
    backgroundFill: new Fill({
      color: "rgba(0, 0, 0, 0.4)",
    }),
    padding: [2, 2, 2, 2],
    textBaseline: "bottom",
    offsetY: -12,
  }),
  image: new RegularShape({
    radius: 6,
    points: 3,
    angle: Math.PI,
    displacement: [0, 8],
    fill: new Fill({
      color: "rgba(0, 0, 0, 0.4)",
    }),
  }),
});

const editSegmentStyles = [editSegmentStyle];

const formatLength = function (line) {
  const length = getLength(line, {
    projection: window["map"].getView().getProjection().getCode(),
  });
  let output;
  if (length > 100) {
    output = Math.round((length / 1000) * 100) / 100 + " km";
  } else {
    output = Math.round(length * 100) / 100 + " m";
  }
  return output;
};

const formatArea = function (polygon) {
  const area = getArea(polygon, {
    projection: window["map"].getView().getProjection().getCode(),
  });

  let output;
  if (area > 10000) {
    output = Math.round((area / 1000000) * 100) / 100 + " km\xB2";
  } else {
    output = Math.round(area * 100) / 100 + " m\xB2";
  }
  return output;
};

let tipPoint;

function editStyleFunction(feature, segments, drawType, tip) {
  const styles = [editStyle];
  const geometry = feature.getGeometry();
  const type = geometry.getType();

  let point, label, line;
  if (!drawType || drawType === type) {
    if (type === "Polygon") {
      point = geometry.getInteriorPoint();
      label = formatArea(geometry);
      line = new LineString(geometry.getCoordinates()[0]);
    } else if (type === "LineString") {
      point = new Point(geometry.getLastCoordinate());
      label = formatLength(geometry);
      line = geometry;
    }
  }
  if (segments && line) {
    let count = 0;
    line.forEachSegment(function (a, b) {
      const segment = new LineString([a, b]);
      const label = formatLength(segment);
      if (editSegmentStyles.length - 1 < count) {
        editSegmentStyles.push(editSegmentStyle.clone());
      }
      const segmentPoint = new Point(segment.getCoordinateAt(0.5));
      editSegmentStyles[count].setGeometry(segmentPoint);
      editSegmentStyles[count].getText().setText(label);
      styles.push(editSegmentStyles[count]);
      count++;
    });
  }
  if (label) {
    editLabelStyle.setGeometry(point);
    editLabelStyle.getText().setText(label);
    styles.push(editLabelStyle);
  }
  if (
    tip &&
    type === "Point" &&
    !window["editModify"].getOverlay().getSource().getFeatures().length
  ) {
    tipPoint = geometry;
    editTipStyle.getText().setText(tip);
    styles.push(editTipStyle);
  }
  return styles;
}

export function editInteraction(type, callback) {
  removeInteraction("select");

  if (window["editModify"]) {
    window["map"].removeInteraction(window["editModify"]);
  }
  if (window["editDraw"]) {
    window["map"].removeInteraction(window["editDraw"]);
  }
  window["editModify"] = null;
  window["editDraw"] = null;

  window["editModify"] = new Modify({
    source: window["editSource"],
    style: editModifyStyle,
  });

  window["editLayer"].setStyle((feature) => {
    return editStyleFunction(feature, showSegments);
  });

  window["map"].addInteraction(window["editModify"]);

  const drawType = type;
  const activeTip =
    trans("Click to continue drawing the ") +
    (drawType === "Polygon" ? "polygon" : "line");
  const idleTip = "Click to start drawing";
  let tip = idleTip;
  window["editDraw"] = new Draw({
    source: window["editSource"],
    type: drawType,
    style: function (feature) {
      return editStyleFunction(feature, showSegments, drawType, tip);
    },
  });
  window["editDraw"].on("drawstart", function () {
    if (clearPrevious) {
      window["editSource"].clear();
    }
    window["editModify"].setActive(false);
    tip = activeTip;
  });
  window["editDraw"].on("drawend", function (e) {
    editModifyStyle.setGeometry(tipPoint);
    window["editModify"].setActive(true);
    window["map"].once("pointermove", function () {
      editModifyStyle.setGeometry();
    });
    tip = idleTip;
    let _featureId = key();
    e.feature.setId(_featureId);

    let _feature = FeatureToGeoJSON(e.feature);
    _feature.projection = window["map"].getView().getProjection().getCode();

    let feature = {
      _featureId: _featureId,
      _feature: _feature,
      // type: type === "Polygon" ? "Area" : "Distance",
      // measurement:
      //   type === "Polygon"
      //     ? formatArea(e.feature.getGeometry())
      //     : formatLength(e.feature.getGeometry()),
    };

    setTimeout(() => {
      window["editSource"].clear();
    }, 1000);
    window["map"].removeInteraction(window["editDraw"]);

    callback(feature);

    window["map"].removeInteraction(window["editDraw"]);
    //store.dispatch({ type: "map/measurements/add", payload: feature });
  });
  //window["editModify"].setActive(true);
  window["map"].addInteraction(window["editDraw"]);
}

// export function removeInteraction() {
//   window["map"].removeInteraction(window["editDraw"]);
// }

export function showEditFeature(feature, type) {
  const json = new GeoJSON({
    dataProjection: getProjectionCode(feature.projection),
    featureProjection: window["map"].getView().getProjection().getCode(),
  }).readFeature(feature);

  const vectorSource = new VectorSource({
    features: [json],
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: function (feature) {
      return editStyleFunction(
        json,
        showSegments,
        json.getGeometry().getType(),
        null
      );
    },
  });

  window["map"].addLayer(vectorLayer);

  window["map"].getView().fit(vectorSource.getExtent(), { maxZoom: 18 });

  setTimeout(() => {
    vectorLayer.getSource().removeFeature(json);
  }, 3000);
}

export function removeInteraction(interactionId) {
  window["map"].getInteractions().forEach((el) => {
    if (el.inderactionId === interactionId) {
      window["map"].removeInteraction(el);
    }
  });
}

export function getInteraction(interactionId) {
  let interaction = null;
  window["map"].getInteractions().forEach((el) => {
    if (el.inderactionId === interactionId) {
      interaction = el;
    }
  });

  return interaction;
}

export function mergeFeatures(features, primaryFeature) {
  let newGeometry = [];
}
